import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, GraduationCap, UserCog, BarChart3,
  Ticket, Settings, CreditCard, Brain, Mail, FileText,
  LogOut, LogIn, Menu, X, ChevronRight, Bell, Search,
  TrendingUp, AlertCircle, CheckCircle, Clock, Plus,
  Shield, Activity, Building, Flag, FileWarning, HelpCircle,
  Database, Lock, Eye, Trash2, RefreshCw, Phone, MessageCircle, Instagram,
  Download, User, Star, Lightbulb, Heart, Zap, BookOpen, Trophy, Compass,
  Copy, KeyRound, RotateCcw, Smile, Calendar, Sparkles, Pencil, Save,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import AdminCallingManagement from '../components/AdminCallingManagement';
import AdminObserverPhones from '../components/AdminObserverPhones';
import AdminOperationsTracker from '../components/AdminOperationsTracker';
import AdminWhatsAppTest from '../components/AdminWhatsAppTest';
import AdminEmailSettings from '../components/AdminEmailSettings';
import AdminSocialMedia from '../components/AdminSocialMedia';
import AdminDataExport from '../components/AdminDataExport';
import AdminCommunications from './AdminCommunications';
import InquiriesManager from '../components/InquiriesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Navigation Items
const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'inquiries', label: 'Inquiries', icon: Mail },
  { id: 'ops-tracker', label: 'Operations Tracker', icon: BarChart3 },
  { id: 'schools', label: 'Schools & Programs', icon: Building },
  { id: 'students', label: 'Student Enrollment', icon: GraduationCap },
  { id: 'users', label: 'User Management', icon: Users },
  { id: 'calling', label: 'Calling', icon: Phone },
  { id: 'observer-phones', label: 'Observer Phones', icon: Phone },
  { id: 'incidents', label: 'Incidents', icon: FileWarning },
  { id: 'support', label: 'Support Tickets', icon: Ticket },
  { id: 'social', label: 'Social Media', icon: Instagram },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'ai-settings', label: 'AI System', icon: Brain },
  { id: 'ai-guardrails', label: 'AI Guardrails', icon: Lock },
  { id: 'privacy', label: 'Data Privacy', icon: Database },
  { id: 'audit', label: 'Audit Logs', icon: Eye },
  { id: 'system', label: 'System Health', icon: Activity },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'help', label: 'Help & FAQs', icon: HelpCircle },
  { id: 'communications', label: 'Communications', icon: Mail },
  { id: 'whatsapp-test', label: 'WhatsApp Test', icon: MessageCircle },
  { id: 'email-settings', label: 'Email Settings', icon: Mail },
  { id: 'data-export', label: 'Data Export', icon: Download },
];

// Wrapper that loads inquiries and renders the InquiriesManager.
const InquiriesSection = () => {
  const [inquiries, setInquiries] = React.useState([]);
  const load = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/inquiries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      setInquiries(data.inquiries || []);
    } catch (e) {
      setInquiries([]);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  return <InquiriesManager inquiries={inquiries} loadInquiries={load} />;
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDashboardData();
    fetchNotifications();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  // ===== Search =====
  const searchTimerRef = useRef(null);

  const runSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/search?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      setSearchResults(res.data?.results || []);
      setSearchOpen(true);
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => runSearch(value), 250);
  };

  const handleSearchResultClick = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    if (result.section) setActiveSection(result.section);
  };

  // ===== Notifications =====
  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/notifications`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      setNotifications(res.data?.notifications || []);
    } catch (e) {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  // Close dropdowns on Escape key or backdrop click (no effect-based listeners needed)

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview stats={stats} onNavigate={setActiveSection} />;
      case 'ops-tracker':
        return <AdminOperationsTracker />;
      case 'schools':
        return <SchoolsManagement />;
      case 'students':
        return <StudentEnrollment />;
      case 'users':
        return <UserManagement />;
      case 'calling':
        return <AdminCallingManagement />;
      case 'observer-phones':
        return <AdminObserverPhones />;
      case 'incidents':
        return <IncidentManagement />;
      case 'support':
        return <SupportManagement />;
      case 'social':
        return <AdminSocialMedia />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'ai-settings':
        return <AISystemSettings />;
      case 'ai-guardrails':
        return <AIGuardrails />;
      case 'privacy':
        return <DataPrivacy />;
      case 'audit':
        return <AuditLogs />;
      case 'system':
        return <SystemHealth />;
      case 'billing':
        return <BillingManagement />;
      case 'help':
        return <HelpFAQs />;
      case 'communications':
        return <AdminCommunications />;
      case 'inquiries':
        return <InquiriesSection />;
      case 'whatsapp-test':
        return <AdminWhatsAppTest />;
      case 'email-settings':
        return <AdminEmailSettings />;
      case 'data-export':
        return <AdminDataExport />;
      default:
        return <DashboardOverview stats={stats} onNavigate={setActiveSection} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile sidebar backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar — fixed overlay on mobile, static in flex on desktop */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 bg-gray-900 text-white flex flex-col transition-all duration-300
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:translate-x-0 md:inset-y-auto md:left-auto md:z-auto
        ${sidebarOpen ? 'w-64' : 'w-64 md:w-20'}
      `}>
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            {(sidebarOpen || mobileMenuOpen) && <h1 className="text-xl font-bold text-orange-400">Sanjaya Admin</h1>}
            <Button variant="ghost" size="icon" onClick={() => { setSidebarOpen(!sidebarOpen); setMobileMenuOpen(false); }} className="text-gray-400 hover:text-white hidden md:flex">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white md:hidden">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                activeSection === item.id
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || mobileMenuOpen) && <span className="text-sm font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-all"
          >
            <LogOut className="w-5 h-5" />
            {(sidebarOpen || mobileMenuOpen) && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto min-w-0">
        {/* Backdrop to close dropdowns on outside click */}
        {(searchOpen || notifOpen) && (
          <div
            className="fixed inset-0 z-30"
            onClick={() => { setSearchOpen(false); setNotifOpen(false); }}
            data-testid="admin-dropdown-backdrop"
          />
        )}
        {/* Top Header */}
        <header className="bg-white border-b px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-40">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile hamburger */}
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)} className="md:hidden flex-shrink-0">
                <Menu className="w-5 h-5" />
              </Button>
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 capitalize truncate">
                {navItems.find(n => n.id === activeSection)?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              {/* Global Search — hidden on small mobile, visible from sm */}
              <div className="relative hidden sm:block" data-testid="admin-search-wrap">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <Input
                  placeholder="Search students, users..."
                  className="pl-9 w-48 md:w-72"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  data-testid="admin-search-input"
                />
                {searchOpen && (searchQuery.trim().length >= 2) && (
                  <div
                    className="absolute right-0 left-0 top-12 bg-white border border-gray-200 rounded-xl shadow-xl max-h-96 overflow-auto z-50"
                    data-testid="admin-search-dropdown"
                  >
                    {searchLoading && (
                      <div className="p-4 text-sm text-gray-500">Searching...</div>
                    )}
                    {!searchLoading && searchResults.length === 0 && (
                      <div className="p-4 text-sm text-gray-500">No results for &ldquo;{searchQuery}&rdquo;</div>
                    )}
                    {!searchLoading && searchResults.map((r, i) => (
                      <button
                        key={`${r.type}-${r.id || i}`}
                        onClick={() => handleSearchResultClick(r)}
                        className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-3"
                        data-testid={`admin-search-result-${i}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                          <div className="text-xs text-gray-500 truncate">{r.subtitle}</div>
                        </div>
                        <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {r.type}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Notifications */}
              <div className="relative" data-testid="admin-notif-wrap">
                <Button
                  variant="outline"
                  size="icon"
                  className="relative"
                  onClick={() => {
                    setNotifOpen((prev) => {
                      if (!prev) fetchNotifications();
                      return !prev;
                    });
                  }}
                  data-testid="admin-notif-button"
                >
                  <Bell className="w-5 h-5" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  )}
                </Button>
                {notifOpen && (
                  <div
                    className="absolute right-0 top-12 w-80 bg-white border border-gray-200 rounded-xl shadow-xl max-h-[28rem] overflow-auto z-50"
                    data-testid="admin-notif-dropdown"
                  >
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">Notifications</span>
                      <button
                        onClick={fetchNotifications}
                        className="text-xs text-orange-600 hover:text-orange-700"
                        data-testid="admin-notif-refresh"
                      >
                        Refresh
                      </button>
                    </div>
                    {notifLoading && (
                      <div className="p-4 text-sm text-gray-500">Loading...</div>
                    )}
                    {!notifLoading && notifications.length === 0 && (
                      <div className="p-6 text-center text-sm text-gray-500">You&apos;re all caught up!</div>
                    )}
                    {!notifLoading && notifications.map((n, i) => {
                      const sevColor =
                        n.severity === 'critical'
                          ? 'bg-red-500'
                          : n.severity === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-orange-400';
                      return (
                        <button
                          key={n.id || i}
                          onClick={() => {
                            setNotifOpen(false);
                            if (n.section) setActiveSection(n.section);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-100 last:border-b-0 flex items-start gap-3"
                          data-testid={`admin-notif-item-${i}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${sevColor} mt-1.5 flex-shrink-0`}></span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 line-clamp-1">{n.title}</div>
                            {n.subtitle && (
                              <div className="text-xs text-gray-500 line-clamp-1">{n.subtitle}</div>
                            )}
                            {n.timestamp && (
                              <div className="text-[10px] text-gray-400 mt-0.5">
                                {new Date(n.timestamp).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pl-2 sm:pl-4 border-l">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-600 font-semibold text-sm">A</span>
                </div>
                <span className="text-sm font-medium hidden sm:inline">{localStorage.getItem('admin_username') || 'Admin'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview({ stats, onNavigate }) {
  if (!stats) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('students')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-3xl font-bold text-gray-900">{stats.students?.total || 0}</p>
                <p className="text-xs text-green-600 mt-1">{stats.students?.active || 0} active</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('users')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">{stats.users?.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.users?.principals || 0}P / {stats.users?.observers || 0}O / {stats.users?.parents || 0}Pa
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sessions Today</p>
                <p className="text-3xl font-bold text-gray-900">{stats.sessions?.today || 0}</p>
                <p className="text-xs text-gray-500 mt-1">{stats.sessions?.total || 0} total</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('support')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Open Tickets</p>
                <p className="text-3xl font-bold text-gray-900">{stats.support?.open_tickets || 0}</p>
                <p className="text-xs text-amber-600 mt-1">{stats.support?.pending_tickets || 0} in progress</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Ticket className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button onClick={() => onNavigate('students')} className="h-20 flex-col gap-2 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white">
              <Plus className="w-5 h-5" />
              <span>Enroll Student</span>
            </Button>
            <Button onClick={() => onNavigate('users')} variant="outline" className="h-20 flex-col gap-2">
              <UserCog className="w-5 h-5" />
              <span>Add User</span>
            </Button>
            <Button onClick={() => onNavigate('support')} variant="outline" className="h-20 flex-col gap-2">
              <Ticket className="w-5 h-5" />
              <span>View Tickets</span>
            </Button>
            <Button onClick={() => onNavigate('ops-tracker')} variant="outline" className="h-20 flex-col gap-2">
              <BarChart3 className="w-5 h-5" />
              <span>View Reports</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>New Inquiries</CardTitle>
            <CardDescription>{stats.inquiries?.new || 0} pending review</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-sm">View all inquiries from the website contact forms</p>
            <Button variant="link" className="px-0 mt-2 text-orange-600 hover:text-orange-700" onClick={() => onNavigate('inquiries')} data-testid="dash-view-inquiries">
              View Inquiries <ChevronRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>System Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">AI System</span>
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" /> Active
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Email Service</span>
              <span className="flex items-center gap-1 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" /> Configure
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Database</span>
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" /> Connected
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Student Evolving Profile Modal ───────────────────────────────────────────

const PROFILE_SECTIONS = [
  { key: 'strengths',    label: 'Strengths',    icon: Star,      color: '#254B70' },
  { key: 'interests',    label: 'Interests',    icon: Lightbulb, color: '#258010' },
  { key: 'personality',  label: 'Personality',  icon: Heart,     color: '#7C3AED' },
  { key: 'capabilities', label: 'Capabilities', icon: Zap,       color: '#1D4ED8' },
  { key: 'experience',   label: 'Experience',   icon: BookOpen,  color: '#F0A820' },
  { key: 'achievements', label: 'Achievements', icon: Trophy,    color: '#DC2626' },
  { key: 'ambitions',    label: 'Ambitions',    icon: Compass,   color: '#0891B2' },
  { key: 'key_activities', label: 'Key Activities & Moments', icon: Sparkles, color: '#EA580C' },
];

const EMOTIONAL_TREND_STYLES = {
  improving: { label: 'Improving', color: '#258010' },
  stable: { label: 'Stable', color: '#F0A820' },
  needs_attention: { label: 'Needs Attention', color: '#DC2626' },
};

function StudentProfileModal({ student, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/admin/student-profile/${student.id}`,
          getAuthHeaders()
        );
        setProfile(data);
      } catch (err) {
        console.error('Failed to load profile', err);
        setProfile({});
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [student.id]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/student-profile/${student.id}/pdf`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${student.name.replace(/ /g, '_')}_profile.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download failed', description: 'Could not generate PDF.', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const totalSessions = profile?.total_sessions ?? 0;
  const lastUpdated   = profile?.last_updated
    ? new Date(profile.last_updated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const hasAnyData = profile && (
    PROFILE_SECTIONS.some(s => (profile[s.key] || []).length > 0) ||
    !!profile?.emotional_growth?.narrative ||
    (profile?.score_history || []).length > 0
  );

  const basicInfo = profile?.basic_info || {};
  const chartData = (profile?.score_history || []).map((h, i) => ({
    date: h.date ? new Date(h.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : `#${i + 1}`,
    'Growth Score': h.growth_score ?? null,
    'Curiosity Index': h.curiosity_score ?? null,
  }));
  const emotionalTrend = EMOTIONAL_TREND_STYLES[profile?.emotional_growth?.trend] || EMOTIONAL_TREND_STYLES.stable;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-sanjaya-navy flex items-center justify-center shrink-0">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{student.name}</h2>
              <p className="text-sm text-gray-500">
                {student.grade || '—'} &nbsp;·&nbsp; {student.school || '—'}
              </p>
              {lastUpdated && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Updated {lastUpdated} &nbsp;·&nbsp; {totalSessions} session{totalSessions !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloading || loading}
              className="bg-sanjaya-navy hover:bg-blue-900 text-white gap-2"
            >
              <Download className="w-4 h-4" />
              {downloading ? 'Generating…' : 'Download PDF'}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mr-3" /> Loading profile…
            </div>
          ) : !hasAnyData ? (
            <div className="text-center py-16 space-y-3">
              <Brain className="w-12 h-12 text-gray-300 mx-auto" />
              <p className="font-medium text-gray-500">No profile data yet</p>
              <p className="text-sm text-gray-400 max-w-xs mx-auto">
                This profile will be automatically built after {student.name.split(' ')[0]}'s first observer session is analysed.
              </p>
            </div>
          ) : (
            <>
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-sanjaya-navy shrink-0" />
                  <span className="text-sm text-gray-600">Age: <span className="font-medium text-gray-900">{basicInfo.age ?? '—'}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-sanjaya-navy shrink-0" />
                  <span className="text-sm text-gray-600">Gender: <span className="font-medium text-gray-900">{basicInfo.gender || '—'}</span></span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <Users className="w-4 h-4 text-sanjaya-navy shrink-0" />
                  <span className="text-sm text-gray-600">
                    Parent: <span className="font-medium text-gray-900">{basicInfo.parent_name || '—'}</span>
                    {basicInfo.parent_phone && <span className="text-gray-400"> &nbsp;·&nbsp; {basicInfo.parent_phone}</span>}
                  </span>
                </div>
              </div>

              {/* Growth & Curiosity trend */}
              {chartData.length > 1 && (
                <div className="p-4 rounded-xl border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-sanjaya-navy" />
                    <span className="font-semibold text-sm text-gray-800">Growth & Curiosity Trend</span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="Growth Score" stroke="#254B70" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line type="monotone" dataKey="Curiosity Index" stroke="#258010" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Emotional Growth */}
              {profile?.emotional_growth?.narrative && (
                <div>
                  <div
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg mb-2"
                    style={{ backgroundColor: '#7C3AED18' }}
                  >
                    <div className="flex items-center gap-2">
                      <Smile className="w-4 h-4 shrink-0" style={{ color: '#7C3AED' }} />
                      <span className="font-semibold text-sm" style={{ color: '#7C3AED' }}>Emotional Growth</span>
                    </div>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: emotionalTrend.color }}
                    >
                      {emotionalTrend.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 pl-9">{profile.emotional_growth.narrative}</p>
                </div>
              )}

              {PROFILE_SECTIONS.map(({ key, label, icon: Icon, color }) => {
                const items = profile?.[key] || [];
                return (
                  <div key={key}>
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded-lg mb-2"
                      style={{ backgroundColor: `${color}18` }}
                    >
                      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                      <span className="font-semibold text-sm" style={{ color }}>{label}</span>
                    </div>
                    {items.length === 0 ? (
                      <p className="text-sm text-gray-400 italic pl-9">Not yet observed</p>
                    ) : (
                      <ul className="space-y-1 pl-9">
                        {items.map((item, i) => (
                          <li key={i} className="text-sm text-gray-700 flex gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Edit Student Modal ───────────────────────────────────────────────────────

function EditStudentModal({ student, principals, observers, onClose, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: student.name || '',
    date_of_birth: student.date_of_birth || '',
    grade: student.grade || '',
    school: student.school || '',
    status: student.status || 'active',
    principal_id: student.principal_id || '',
    observer_id: student.observer_id || '',
    notes: student.notes || '',
    parent_name: student.parents?.[0]?.name || '',
    parent_email: student.parents?.[0]?.email || '',
    parent_phone: student.parents?.[0]?.phone || '',
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  });

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const params = new URLSearchParams(form);
      await axios.put(`${API_URL}/api/admin/students/${student.id}?${params}`, null, getAuthHeaders());
      toast({ title: 'Student updated', description: `${form.name}'s details were saved.` });
      onSaved();
      onClose();
    } catch (error) {
      toast({ title: 'Update failed', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-sanjaya-navy flex items-center justify-center shrink-0">
              <Pencil className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Edit {student.name}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Student Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Full Name *</label>
                <Input value={form.name} onChange={set('name')} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Date of Birth</label>
                <Input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Grade</label>
                <Input value={form.grade} onChange={set('grade')} />
              </div>
              <div>
                <label className="text-xs text-gray-500">School</label>
                <Input value={form.school} onChange={set('school')} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Status</label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.status}
                  onChange={set('status')}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending_payment">Pending Payment</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Parent / Guardian</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Parent Name</label>
                <Input value={form.parent_name} onChange={set('parent_name')} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Parent Phone</label>
                <Input value={form.parent_phone} onChange={set('parent_phone')} />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500">Parent Email</label>
                <Input type="email" value={form.parent_email} onChange={set('parent_email')} />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Assignments</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Principal</label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.principal_id}
                  onChange={set('principal_id')}
                >
                  <option value="">Not assigned</option>
                  {principals.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Observer</label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.observer_id}
                  onChange={set('observer_id')}
                >
                  <option value="">Not assigned</option>
                  {observers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500">Notes</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px]"
              value={form.notes}
              onChange={set('notes')}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-sanjaya-navy hover:bg-blue-900 text-white gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Student Enrollment Component
function StudentEnrollment() {
  const { toast } = useToast();
  const [students, setStudents] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [observers, setObservers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [profileStudent, setProfileStudent] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // credMap: { [studentId]: { open, loading, email, password, resetting } }
  const [credMap, setCredMap] = useState({});
  const [formData, setFormData] = useState({
    name: '', date_of_birth: '', grade: '', school: '',
    parent_email: '', parent_name: '', parent_phone: '',
    principal_id: '', observer_id: '', notes: ''
  });

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, principalsRes, observersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/students`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/users/principals`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/users/observers`, getAuthHeaders())
      ]);
      setStudents(studentsRes.data.students || []);
      setPrincipals(principalsRes.data.principals || []);
      setObservers(observersRes.data.observers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    try {
      const params = new URLSearchParams(formData);
      const { data } = await axios.post(`${API_URL}/api/admin/students/enroll?${params}`, null, getAuthHeaders());
      toast({ title: "Student enrolled!", description: `${formData.name} has been enrolled successfully.` });
      setShowEnrollForm(false);
      setFormData({ name: '', date_of_birth: '', grade: '', school: '', parent_email: '', parent_name: '', parent_phone: '', principal_id: '', observer_id: '', notes: '' });
      if (data?.parent?.is_new && data?.parent?.credentials) {
        setNewCredentials({
          studentName: data.student.name,
          parentName: data.parent.name,
          email: data.parent.credentials.email,
          password: data.parent.credentials.password,
        });
      }
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to enroll", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/admin/students/${deleteTarget.id}`,
        getAuthHeaders(),
      );
      const cascaded = Object.entries(data.summary || {})
        .filter(([k, v]) => k !== 'student' && typeof v === 'number' && v > 0)
        .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
        .join(', ');
      toast({
        title: `${deleteTarget.name} deleted`,
        description: cascaded
          ? `Also archived: ${cascaded}. Backups kept for restore.`
          : 'Backups kept for restore.',
      });
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const toggleCredentials = async (student) => {
    const sid = student.id;
    // If already open, just close
    if (credMap[sid]?.open) {
      setCredMap(m => ({ ...m, [sid]: { ...m[sid], open: false } }));
      return;
    }
    // If we already fetched creds, just re-open
    if (credMap[sid]?.email) {
      setCredMap(m => ({ ...m, [sid]: { ...m[sid], open: true } }));
      return;
    }
    // Pre-populate from list data if portal already exists
    const existing = student.parents?.[0]?.portal_password_plain;
    const existingEmail = student.parents?.[0]?.email;
    if (existing && existingEmail) {
      setCredMap(m => ({ ...m, [sid]: { open: true, loading: false, email: existingEmail, password: existing, resetting: false } }));
      return;
    }
    // Fetch / generate from backend
    setCredMap(m => ({ ...m, [sid]: { open: true, loading: true, email: null, password: null, resetting: false } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/students/${sid}/parent-portal`, null, getAuthHeaders());
      setCredMap(m => ({ ...m, [sid]: { open: true, loading: false, email: data.email, password: data.password, resetting: false } }));
      if (data.is_new) toast({ title: 'Parent portal created', description: `Credentials generated for ${data.email}` });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not generate credentials', variant: 'destructive' });
      setCredMap(m => ({ ...m, [sid]: { open: false, loading: false, email: null, password: null, resetting: false } }));
    }
  };

  const resetCredentials = async (student) => {
    const sid = student.id;
    setCredMap(m => ({ ...m, [sid]: { ...m[sid], resetting: true } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/students/${sid}/parent-portal/reset`, null, getAuthHeaders());
      setCredMap(m => ({ ...m, [sid]: { open: true, loading: false, email: data.email, password: data.password, resetting: false } }));
      toast({ title: 'Password reset', description: 'New credentials generated successfully.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Reset failed', variant: 'destructive' });
      setCredMap(m => ({ ...m, [sid]: { ...m[sid], resetting: false } }));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() =>
      toast({ title: `${label} copied`, description: text })
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">All Students</h3>
          <p className="text-sm text-gray-500">{students.length} total enrolled</p>
        </div>
        <Button onClick={() => setShowEnrollForm(true)} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white">
          <Plus className="w-4 h-4 mr-2" /> Enroll New Student
        </Button>
      </div>

      {/* Parent credentials banner — shown after a new parent account is created */}
      {newCredentials && (
        <Card className="border-2 border-green-400 bg-green-50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="font-semibold text-green-800 text-base">
                  New parent account created for {newCredentials.studentName}
                </p>
                <p className="text-sm text-green-700">
                  Share these login credentials with <strong>{newCredentials.parentName}</strong>:
                </p>
                <div className="bg-white border border-green-300 rounded-lg p-3 space-y-1 font-mono text-sm">
                  <p><span className="text-gray-500">Email:&nbsp;&nbsp;&nbsp;</span><strong>{newCredentials.email}</strong></p>
                  <p><span className="text-gray-500">Password: </span><strong>{newCredentials.password}</strong></p>
                </div>
                <p className="text-xs text-green-600">The parent can log in at the Parent portal and change their password after first login.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setNewCredentials(null)} className="shrink-0 text-green-700">
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enrollment Form Modal */}
      {showEnrollForm && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle>Enroll New Student</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Student Name *</label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Full name" />
              </div>
              <div>
                <label className="text-sm font-medium">Date of Birth *</label>
                <Input type="date" value={formData.date_of_birth} onChange={e => setFormData({...formData, date_of_birth: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Grade *</label>
                <Input value={formData.grade} onChange={e => setFormData({...formData, grade: e.target.value})} placeholder="e.g., Grade 3" />
              </div>
              <div>
                <label className="text-sm font-medium">School *</label>
                <Input value={formData.school} onChange={e => setFormData({...formData, school: e.target.value})} placeholder="School name" />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Parent/Guardian Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Parent Name *</label>
                  <Input value={formData.parent_name} onChange={e => setFormData({...formData, parent_name: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Parent Email *</label>
                  <Input type="email" value={formData.parent_email} onChange={e => setFormData({...formData, parent_email: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-medium">Parent Phone *</label>
                  <Input value={formData.parent_phone} onChange={e => setFormData({...formData, parent_phone: e.target.value})} />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Assignments (Optional)</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Assign to Principal</label>
                  <select className="w-full border rounded-md p-2" value={formData.principal_id} onChange={e => setFormData({...formData, principal_id: e.target.value})}>
                    <option value="">Select Principal...</option>
                    {principals.map(p => <option key={p.id} value={p.id}>{p.name} - {p.school}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Assign to Observer</label>
                  <select className="w-full border rounded-md p-2" value={formData.observer_id} onChange={e => setFormData({...formData, observer_id: e.target.value})}>
                    <option value="">Select Observer...</option>
                    {observers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleEnroll} className="bg-green-500 hover:bg-green-600">Enroll Student</Button>
              <Button variant="outline" onClick={() => setShowEnrollForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Students Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-gray-600">Student</th>
                <th className="text-left p-4 font-medium text-gray-600">School / Grade</th>
                <th className="text-left p-4 font-medium text-gray-600">Parent</th>
                <th className="text-left p-4 font-medium text-gray-600">Principal</th>
                <th className="text-left p-4 font-medium text-gray-600">Observer</th>
                <th className="text-left p-4 font-medium text-gray-600">Status</th>
                <th className="text-left p-4 font-medium text-gray-600">Profile</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => {
                const cred = credMap[student.id] || {};
                const hasStoredCreds = !!student.parents?.[0]?.portal_password_plain;
                return (
                  <React.Fragment key={student.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <p className="font-medium">{student.name}</p>
                        <p className="text-xs text-gray-500">{student.date_of_birth}</p>
                      </td>
                      <td className="p-4">
                        <p>{student.school || '-'}</p>
                        <p className="text-xs text-gray-500">{student.grade || '-'}</p>
                      </td>
                      <td className="p-4">
                        {student.parents?.[0] ? (
                          <>
                            <p className="text-sm">{student.parents[0].name}</p>
                            <p className="text-xs text-gray-500">{student.parents[0].email}</p>
                          </>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        {student.principal ? student.principal.name : <span className="text-gray-400">Not assigned</span>}
                      </td>
                      <td className="p-4">
                        {student.observer ? student.observer.name : <span className="text-gray-400">Not assigned</span>}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {student.status || 'active'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setProfileStudent(student)}
                            className="gap-1.5 text-xs border-sanjaya-navy text-sanjaya-navy hover:bg-sanjaya-navy hover:text-white"
                          >
                            <User className="w-3.5 h-3.5" />
                            View Profile
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className={`px-2 border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700 ${cred.open ? 'bg-violet-50 border-violet-300 text-violet-600' : ''}`}
                                title="More actions"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={() => setEditTarget(student)} className="gap-2 cursor-pointer">
                                <Pencil className="w-3.5 h-3.5" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleCredentials(student)} className="gap-2 cursor-pointer">
                                <KeyRound className="w-3.5 h-3.5" />
                                {hasStoredCreds || cred.email ? 'Credentials' : 'Create Portal'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(student)}
                                className="gap-2 cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>

                    {/* Credentials expandable row */}
                    {cred.open && (
                      <tr className="bg-violet-50/60 border-b border-violet-100">
                        <td colSpan={7} className="px-6 py-4">
                          {cred.loading ? (
                            <p className="text-sm text-violet-600 flex items-center gap-2">
                              <RefreshCw className="w-4 h-4 animate-spin" /> Generating credentials…
                            </p>
                          ) : (
                            <div className="flex flex-wrap items-center gap-6">
                              <div className="flex items-center gap-2 min-w-0">
                                <KeyRound className="w-4 h-4 text-violet-500 shrink-0" />
                                <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Parent Portal Credentials</span>
                              </div>
                              <div className="flex items-center gap-2 bg-white border border-violet-200 rounded-lg px-3 py-2">
                                <span className="text-xs text-gray-500 shrink-0">Username&nbsp;(Email):</span>
                                <span className="text-sm font-mono font-medium text-gray-800">{cred.email}</span>
                                <button
                                  onClick={() => copyToClipboard(cred.email, 'Email')}
                                  className="ml-1 p-1 rounded hover:bg-violet-100 text-violet-500"
                                  title="Copy email"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <div className="flex items-center gap-2 bg-white border border-violet-200 rounded-lg px-3 py-2">
                                <span className="text-xs text-gray-500 shrink-0">Password:</span>
                                <span className="text-sm font-mono font-medium text-gray-800">{cred.password}</span>
                                <button
                                  onClick={() => copyToClipboard(cred.password, 'Password')}
                                  className="ml-1 p-1 rounded hover:bg-violet-100 text-violet-500"
                                  title="Copy password"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resetCredentials(student)}
                                disabled={cred.resetting}
                                className="gap-1.5 text-xs border-orange-300 text-orange-600 hover:bg-orange-50 shrink-0"
                                title="Generate a new password"
                              >
                                <RotateCcw className={`w-3.5 h-3.5 ${cred.resetting ? 'animate-spin' : ''}`} />
                                Reset Password
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {students.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-gray-500">No students enrolled yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Evolving Profile Modal */}
      {profileStudent && (
        <StudentProfileModal
          student={profileStudent}
          onClose={() => setProfileStudent(null)}
        />
      )}

      {/* Edit Student Modal */}
      {editTarget && (
        <EditStudentModal
          student={editTarget}
          principals={principals}
          observers={observers}
          onClose={() => setEditTarget(null)}
          onSaved={fetchData}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <span className="font-semibold">{deleteTarget?.name}</span> and every
              linked report, insight, and session from all portals (Observer, Parent, Principal, Admin).
              A backup is archived automatically, so an admin can restore later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-student-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="delete-student-confirm"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// User Management Component
function UserManagement() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('principals');
  const [principals, setPrincipals] = useState([]);
  const [observers, setObservers] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [unlinkedChildren, setUnlinkedChildren] = useState([]);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [newChild, setNewChild] = useState({ name: '', age: '', gender: 'male' });

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const [p, o, pa] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users/principals`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/users/observers`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/users/parents`, getAuthHeaders())
      ]);
      setPrincipals(p.data.principals || []);
      setObservers(o.data.observers || []);
      setParents(pa.data.parents || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnlinkedChildren = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/students/unlinked`, getAuthHeaders());
      setUnlinkedChildren(data.children || []);
    } catch (error) {
      setUnlinkedChildren([]);
    }
  };

  const openForm = (type) => {
    setShowForm(type);
    setFormData({});
    setSelectedChildIds([]);
    setNewChild({ name: '', age: '', gender: 'male' });
    if (type === 'parent') {
      fetchUnlinkedChildren();
    }
  };

  const createUser = async (type) => {
    try {
      const payload = { ...formData };
      if (type === 'parent') {
        if (selectedChildIds.length) payload.link_child_ids = selectedChildIds.join(',');
        if (newChild.name.trim()) {
          payload.new_child_name = newChild.name.trim();
          payload.new_child_age = newChild.age || 0;
          payload.new_child_gender = newChild.gender;
        }
      }
      const params = new URLSearchParams(payload);
      const { data } = await axios.post(`${API_URL}/api/admin/users/${type}?${params}`, null, getAuthHeaders());
      const linkedNote = (data?.linked_children?.length || 0) > 0
        ? ` and linked ${data.linked_children.length} child(ren)`
        : '';
      toast({ title: 'Success!', description: `${type} created successfully${linkedNote}` });
      setShowForm(null);
      setFormData({});
      setSelectedChildIds([]);
      setNewChild({ name: '', age: '', gender: 'male' });
      fetchUsers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create user', variant: 'destructive' });
    }
  };

  const deleteUser = async (type, user) => {
    const confirmMsg =
      `Delete ${type} "${user.name}"?\n\n` +
      `This removes the account permanently. ` +
      (type === 'parent'
        ? 'Linked children will be unlinked (not deleted).'
        : type === 'observer'
          ? 'Their phone config will be removed and assigned children will be unassigned.'
          : 'Their assigned observers and children will be unassigned.');
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${type}/${user.id}`, getAuthHeaders());
      toast({ title: 'Deleted', description: `${user.name} removed successfully` });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Could not delete user',
        variant: 'destructive',
      });
    }
  };

  const loginAsPrincipal = async (principal) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/impersonate/principal/${principal.id}`,
        null,
        getAuthHeaders()
      );
      Object.entries(data.storage || {}).forEach(([k, v]) => localStorage.setItem(k, v));
      window.open('/principal/dashboard', '_blank');
      toast({ title: `Logged in as ${principal.name}`, description: 'Principal dashboard opened in a new tab.' });
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.response?.data?.detail || 'Could not impersonate principal',
        variant: 'destructive',
      });
    }
  };

  const loginAsObserver = async (observer) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/impersonate/observer/${observer.id}`,
        null,
        getAuthHeaders()
      );
      // Write the observer session into localStorage
      Object.entries(data.storage || {}).forEach(([k, v]) => localStorage.setItem(k, v));
      // Open the observer dashboard in a new tab so admin session is preserved
      window.open('/observer/dashboard', '_blank');
      toast({ title: `Logged in as ${observer.name}`, description: 'Observer dashboard opened in a new tab.' });
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.response?.data?.detail || 'Could not impersonate observer',
        variant: 'destructive',
      });
    }
  };

  const renderUserList = (users, type) => (
    <div className="space-y-3">
      {users.map(user => (
        <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`user-row-${type}-${user.id}`}>
          <div className="min-w-0">
            <p className="font-medium">{user.name}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
            {type === 'principal' && <p className="text-xs text-gray-400">{user.school} • {user.student_count || 0} students</p>}
            {type === 'observer' && <p className="text-xs text-gray-400">{user.specialization} • {user.assigned_children || 0} children</p>}
            {type === 'parent' && <p className="text-xs text-gray-400">{user.phone || '—'} • {user.children_count || 0} children</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`px-2 py-1 rounded-full text-xs ${user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {user.is_active !== false ? 'Active' : 'Inactive'}
            </span>
            {type === 'principal' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loginAsPrincipal(user)}
                className="h-8 px-3 text-xs border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400 font-medium gap-1"
                data-testid={`login-as-principal-${user.id}`}
                title={`Log in as ${user.name}`}
              >
                <LogIn className="w-3.5 h-3.5" />
                Login
              </Button>
            )}
            {type === 'observer' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loginAsObserver(user)}
                className="h-8 px-3 text-xs border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400 font-medium gap-1"
                data-testid={`login-as-observer-${user.id}`}
                title={`Log in as ${user.name}`}
              >
                <LogIn className="w-3.5 h-3.5" />
                Login
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteUser(type, user)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
              data-testid={`delete-${type}-${user.id}`}
              title={`Delete ${type}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      {users.length === 0 && <p className="text-center text-gray-500 py-8">No {type}s found</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['principals', 'observers', 'parents'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize ${activeTab === tab ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
            data-testid={`user-tab-${tab}`}
          >
            {tab} ({tab === 'principals' ? principals.length : tab === 'observers' ? observers.length : parents.length})
          </button>
        ))}
      </div>

      {/* Add User Button (now also enabled for parents) */}
      <Button
        onClick={() => openForm(activeTab.slice(0, -1))}
        className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white"
        data-testid={`add-${activeTab.slice(0, -1)}-btn`}
      >
        <Plus className="w-4 h-4 mr-2" /> Add {activeTab.slice(0, -1)}
      </Button>

      {/* Add Form */}
      {showForm && (
        <Card className="border-2 border-orange-200 bg-orange-50/40">
          <CardHeader><CardTitle>Add New {showForm}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input placeholder="Name" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} data-testid="user-form-name" />
              <Input placeholder="Email" type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} data-testid="user-form-email" />
              <Input placeholder="Phone" value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} data-testid="user-form-phone" />
              {showForm === 'principal' && <Input placeholder="School Name" value={formData.school || ''} onChange={e => setFormData({...formData, school: e.target.value})} data-testid="user-form-school" />}
              {showForm === 'observer' && <Input placeholder="Specialization" value={formData.specialization || ''} onChange={e => setFormData({...formData, specialization: e.target.value})} data-testid="user-form-specialization" />}
              <Input placeholder="Password" type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} data-testid="user-form-password" />
            </div>

            {/* Child linking section for parents */}
            {showForm === 'parent' && (
              <div className="space-y-4 pt-2 border-t border-orange-200" data-testid="parent-link-child-section">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Link to existing child(ren)
                  </h4>
                  {unlinkedChildren.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">No unlinked children available.</p>
                  ) : (
                    <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
                      {unlinkedChildren.map((c) => {
                        const checked = selectedChildIds.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm"
                            data-testid={`link-child-row-${c.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedChildIds([...selectedChildIds, c.id]);
                                else setSelectedChildIds(selectedChildIds.filter((id) => id !== c.id));
                              }}
                              className="accent-orange-500"
                            />
                            <span className="font-medium">{c.name}</span>
                            <span className="text-xs text-gray-500">
                              {c.age ? `${c.age}y` : '—'} · {c.gender || '—'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Or enrol a new child (optional)
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Input
                      placeholder="Child's name"
                      value={newChild.name}
                      onChange={(e) => setNewChild({ ...newChild, name: e.target.value })}
                      data-testid="new-child-name"
                    />
                    <Input
                      type="number"
                      placeholder="Age"
                      value={newChild.age}
                      onChange={(e) => setNewChild({ ...newChild, age: e.target.value })}
                      data-testid="new-child-age"
                    />
                    <select
                      value={newChild.gender}
                      onChange={(e) => setNewChild({ ...newChild, gender: e.target.value })}
                      data-testid="new-child-gender"
                      className="border rounded-md px-3 py-2 text-sm bg-white"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    A new child created here is auto-enrolled and linked to this parent.
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={() => createUser(showForm)} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white" data-testid="user-form-submit">Create</Button>
              <Button variant="outline" onClick={() => { setShowForm(null); setFormData({}); }} data-testid="user-form-cancel">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Lists */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          {activeTab === 'principals' && renderUserList(principals, 'principal')}
          {activeTab === 'observers' && renderUserList(observers, 'observer')}
          {activeTab === 'parents' && renderUserList(parents, 'parent')}
        </CardContent>
      </Card>
    </div>
  );
}

// Analytics Dashboard Component
function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/analytics/overview?days=30`, getAuthHeaders())
      .then(res => setAnalytics(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8">Loading analytics...</div>;

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Platform Analytics (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <p className="text-4xl font-bold text-blue-600">{analytics?.sessions?.total || 0}</p>
              <p className="text-gray-600">Total Sessions</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <p className="text-4xl font-bold text-green-600">{analytics?.enrollments?.total || 0}</p>
              <p className="text-gray-600">Total Enrollments</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-xl">
              <p className="text-4xl font-bold text-purple-600">{analytics?.schools?.length || 0}</p>
              <p className="text-gray-600">Active Schools</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Schools Overview</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics?.schools?.map((school, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium">{school.school}</span>
                <span className="text-gray-600">{school.students} students</span>
              </div>
            ))}
            {(!analytics?.schools || analytics.schools.length === 0) && (
              <p className="text-center text-gray-500 py-4">No school data available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Support Management Component
function SupportManagement() {
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [roleCounts, setRoleCounts] = useState({ parent: 0, observer: 0, principal: 0 });
  const [totalAll, setTotalAll] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');      // all | parent | observer | principal
  const [statusFilter, setStatusFilter] = useState('all');  // all | open | in_progress | resolved | closed

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => { fetchTickets(); /* eslint-disable-next-line */ }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/support/tickets`, getAuthHeaders());
      setTickets(res.data.tickets || []);
      setStatusCounts(res.data.status_counts || {});
      setRoleCounts(res.data.role_counts || {});
      setTotalAll(res.data.total_all || (res.data.tickets || []).length);
      // Keep selectedTicket up-to-date with latest data from server
      if (selectedTicket) {
        const fresh = (res.data.tickets || []).find(t => t.id === selectedTicket.id);
        if (fresh) setSelectedTicket(fresh);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/support/tickets/${ticketId}?status=${status}`, null, getAuthHeaders());
      toast({ title: "Updated", description: `Ticket marked ${status.replace('_', ' ')}` });
      fetchTickets();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const sendReply = async (ticketId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/support/tickets/${ticketId}/reply?message=${encodeURIComponent(replyText)}`, null, getAuthHeaders());
      toast({ title: "Sent", description: "Reply sent successfully" });
      setReplyText('');
      fetchTickets();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send reply", variant: "destructive" });
    }
  };

  // Apply role + status filters in the client; counts always reflect ALL tickets
  const filteredTickets = tickets.filter(t =>
    (roleFilter === 'all' || t.user_role === roleFilter) &&
    (statusFilter === 'all' || t.status === statusFilter)
  );

  const STATUS_CARDS = [
    { key: 'open', label: 'Open', count: statusCounts.open || 0, color: 'text-blue-700 bg-blue-50 border-blue-200' },
    { key: 'in_progress', label: 'In Progress', count: statusCounts.in_progress || 0, color: 'text-amber-700 bg-amber-50 border-amber-200' },
    { key: 'resolved', label: 'Resolved', count: statusCounts.resolved || 0, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    { key: 'closed', label: 'Closed', count: statusCounts.closed || 0, color: 'text-gray-700 bg-gray-100 border-gray-200' },
  ];

  const ROLE_TABS = [
    { key: 'all', label: 'All Roles', count: totalAll, color: 'bg-orange-100 text-orange-700 border-orange-300' },
    { key: 'parent', label: 'Parents', count: roleCounts.parent || 0, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { key: 'observer', label: 'Observers', count: roleCounts.observer || 0, color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { key: 'principal', label: 'Principals', count: roleCounts.principal || 0, color: 'bg-purple-100 text-purple-700 border-purple-300' },
  ];

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading tickets…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-support-root">
      {/* Status count cards (always show totals across ALL tickets) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STATUS_CARDS.map(card => (
          <button
            key={card.key}
            data-testid={`status-card-${card.key}`}
            onClick={() => setStatusFilter(statusFilter === card.key ? 'all' : card.key)}
            className={`text-left border rounded-xl p-4 transition-all ${card.color} ${statusFilter === card.key ? 'ring-2 ring-offset-1 ring-orange-500' : 'hover:shadow-sm'}`}
          >
            <div className="text-xs uppercase tracking-wide font-semibold opacity-80">{card.label}</div>
            <div className="text-3xl font-bold mt-1">{card.count}</div>
          </button>
        ))}
      </div>

      {/* Role filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="role-tabs">
        {ROLE_TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`role-tab-${tab.key}`}
            onClick={() => setRoleFilter(tab.key)}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition-all flex items-center gap-2 ${
              roleFilter === tab.key
                ? `${tab.color} shadow-sm`
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <span>{tab.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${roleFilter === tab.key ? 'bg-white/80' : 'bg-gray-100'}`}>
              {tab.count}
            </span>
          </button>
        ))}
        {(roleFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => { setRoleFilter('all'); setStatusFilter('all'); }}
            className="px-3 py-2 rounded-full border border-dashed border-gray-300 text-xs text-gray-500 hover:bg-gray-50"
            data-testid="clear-filters"
          >
            Clear filters ×
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tickets List */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>
              Tickets
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({filteredTickets.length} shown
                {roleFilter !== 'all' || statusFilter !== 'all' ? ` • filtered from ${totalAll}` : ''})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto p-0">
            {filteredTickets.length === 0 ? (
              <p className="text-center text-gray-500 py-12">No tickets match these filters.</p>
            ) : (
              <ul className="divide-y">
                {filteredTickets.map(ticket => {
                  const roleStyle =
                    ticket.user_role === 'parent' ? 'bg-emerald-100 text-emerald-700' :
                    ticket.user_role === 'observer' ? 'bg-blue-100 text-blue-700' :
                    ticket.user_role === 'principal' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-100 text-gray-700';
                  const statusStyle =
                    ticket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                    ticket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                    ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-gray-200 text-gray-700';
                  return (
                    <li
                      key={ticket.id}
                      onClick={() => setSelectedTicket(ticket)}
                      data-testid={`admin-ticket-row-${ticket.id}`}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedTicket?.id === ticket.id ? 'bg-orange-50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{ticket.subject}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {ticket.user_name || ticket.user_email} • {ticket.ticket_number}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(ticket.created_at).toLocaleDateString()} • {ticket.category}
                            {ticket.responses?.length > 0 && ` • ${ticket.responses.length} reply${ticket.responses.length === 1 ? '' : 'ies'}`}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${roleStyle}`}>{ticket.user_role}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${statusStyle}`}>{(ticket.status || '').replace('_', ' ')}</span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Ticket Detail */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Ticket Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedTicket ? (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${
                      selectedTicket.user_role === 'parent' ? 'bg-emerald-100 text-emerald-700' :
                      selectedTicket.user_role === 'observer' ? 'bg-blue-100 text-blue-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>{selectedTicket.user_role}</span>
                    <span className="text-xs text-gray-500">{selectedTicket.ticket_number}</span>
                  </div>
                  <p className="font-medium text-lg">{selectedTicket.subject}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedTicket.user_name} ({selectedTicket.user_email}) • {selectedTicket.category} • Priority: {selectedTicket.priority}
                  </p>
                </div>

                {/* Conversation thread (description first, then chronological replies) */}
                <div className="border-t pt-4 space-y-2 max-h-72 overflow-y-auto bg-gray-50 rounded-lg p-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">{selectedTicket.user_name || selectedTicket.user_role} • Original message</p>
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>
                  {(selectedTicket.responses || []).map((r, i) => (
                    <div key={r.id || i} className={`rounded-lg p-3 border ${r.user_role === 'admin' ? 'bg-orange-50 border-orange-200 ml-6' : 'bg-white border-gray-200 mr-6'}`}>
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {r.user_role === 'admin' ? 'Sanjaya Support (Admin)' : `${r.user_name || r.user_role}`}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>

                {/* Status controls */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')} data-testid="status-in-progress">
                      Mark In Progress
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="status-resolved">
                      Mark Resolved
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'closed')} data-testid="status-closed">
                      Close
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'open')} data-testid="status-reopen">
                      Re-open
                    </Button>
                  </div>
                </div>

                {/* Admin reply box */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Reply to User</p>
                  <textarea
                    className="w-full border rounded p-2 text-sm"
                    rows={3}
                    placeholder="Type your reply…"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    data-testid="admin-reply-input"
                  />
                  <Button size="sm" onClick={() => sendReply(selectedTicket.id)} className="mt-2 bg-orange-500 hover:bg-orange-600" data-testid="admin-reply-send">
                    Send Reply
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-12">Select a ticket to view conversation and reply.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// AI System Settings Component
function AISystemSettings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/ai/settings`, getAuthHeaders())
      .then(res => setSettings(res.data.settings))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateSetting = async (key, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/ai/settings?${key}=${value}`, null, getAuthHeaders());
      setSettings({...settings, [key]: value});
      toast({ title: "Updated", description: "Setting saved" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>AI System Configuration</CardTitle>
          <CardDescription>Configure how the AI processes session data and generates reports</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {[
            { key: 'behavioral_tags_enabled', label: 'Behavioral Tags Extraction', desc: 'Automatically extract behavioral tags from session logs' },
            { key: 'auto_report_generation', label: 'Auto Report Generation', desc: 'Generate reports automatically when enough sessions are logged' },
            { key: 'trend_analysis_enabled', label: 'Trend Analysis', desc: 'Analyze trends and patterns across sessions' },
            { key: 'sentiment_analysis_enabled', label: 'Sentiment Analysis', desc: 'Analyze emotional sentiment in session notes' },
            { key: 'notification_on_concerns', label: 'Concern Notifications', desc: 'Send alerts when concerning patterns are detected' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
              <button
                onClick={() => updateSetting(item.key, !settings?.[item.key])}
                className={`w-12 h-6 rounded-full transition-colors ${settings?.[item.key] ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${settings?.[item.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <label className="font-medium">Minimum Sessions for Report</label>
            <p className="text-sm text-gray-500 mb-2">Number of sessions required before generating a report</p>
            <Input
              type="number"
              value={settings?.min_sessions_for_report || 3}
              onChange={e => updateSetting('min_sessions_for_report', parseInt(e.target.value))}
              className="w-24"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Billing Management Component
function BillingManagement() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/admin/billing/subscriptions`, getAuthHeaders()),
      axios.get(`${API_URL}/api/admin/billing/payments`, getAuthHeaders())
    ]).then(([subs, pays]) => {
      setSubscriptions(subs.data.subscriptions || []);
      setPayments(pays.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Collected</p>
            <p className="text-3xl font-bold text-green-600">₹{payments.total_collected || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-amber-50">
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Pending Amount</p>
            <p className="text-3xl font-bold text-amber-600">₹{payments.pending_amount || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Active Subscriptions</p>
            <p className="text-3xl font-bold text-blue-600">{subscriptions.filter(s => s.status === 'active').length}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Subscriptions</CardTitle></CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No subscriptions yet. Subscriptions will appear here when parents enroll their children.</p>
          ) : (
            <div className="space-y-3">
              {subscriptions.map(sub => (
                <div key={sub.id} className="p-4 bg-gray-50 rounded-lg flex justify-between">
                  <div>
                    <p className="font-medium">{sub.plan}</p>
                    <p className="text-sm text-gray-500">{sub.start_date} - {sub.end_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">₹{sub.amount}</p>
                    <span className={`text-xs px-2 py-1 rounded ${sub.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {sub.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Schools Management Component
function SchoolsManagement() {
  const { toast } = useToast();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '', city: '', contact_email: '', contact_phone: '' });

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/schools`, getAuthHeaders())
      .then(res => setSchools(res.data.schools || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const createSchool = async () => {
    try {
      const params = new URLSearchParams(formData);
      await axios.post(`${API_URL}/api/admin/schools?${params}`, null, getAuthHeaders());
      toast({ title: "Success!", description: "School created" });
      setShowForm(false);
      setFormData({ name: '', address: '', city: '', contact_email: '', contact_phone: '' });
      const res = await axios.get(`${API_URL}/api/admin/schools`, getAuthHeaders());
      setSchools(res.data.schools || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to create school", variant: "destructive" });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Schools & Programs</h3>
          <p className="text-sm text-gray-500">{schools.length} registered</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add School
        </Button>
      </div>

      {showForm && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader><CardTitle>Add New School</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input placeholder="School Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              <Input placeholder="City" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
              <Input placeholder="Address" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
              <Input placeholder="Contact Email" value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} />
              <Input placeholder="Contact Phone" value={formData.contact_phone} onChange={e => setFormData({...formData, contact_phone: e.target.value})} />
            </div>
            <div className="flex gap-3">
              <Button onClick={createSchool} className="bg-green-500 hover:bg-green-600">Create</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {schools.map(school => (
          <Card key={school.id} className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-lg">{school.name}</h4>
                  <p className="text-sm text-gray-500">{school.city}</p>
                  <p className="text-xs text-gray-400">{school.contact_email}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${school.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
                  {school.status || 'active'}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center sm:grid-cols-3">
                <div className="bg-gray-50 p-2 rounded"><p className="text-lg font-bold">{school.student_count || 0}</p><p className="text-xs text-gray-500">Students</p></div>
                <div className="bg-gray-50 p-2 rounded"><p className="text-lg font-bold">{school.principal_count || 0}</p><p className="text-xs text-gray-500">Principals</p></div>
                <div className="bg-gray-50 p-2 rounded"><p className="text-lg font-bold">{school.observer_count || 0}</p><p className="text-xs text-gray-500">Observers</p></div>
              </div>
            </CardContent>
          </Card>
        ))}
        {schools.length === 0 && <p className="text-center text-gray-500 py-8 col-span-2">No schools registered yet</p>}
      </div>
    </div>
  );
}

// Safety & Escalation Component
function SafetyEscalation() {
  const { toast } = useToast();
  const [flags, setFlags] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFlag, setSelectedFlag] = useState(null);

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/safety/red-flags`, getAuthHeaders())
      .then(res => { setFlags(res.data.flags || []); setStatusCounts(res.data.status_counts || {}); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateFlag = async (flagId, status, escalate = false) => {
    try {
      await axios.put(`${API_URL}/api/admin/safety/red-flag/${flagId}?status=${status}&escalate=${escalate}`, null, getAuthHeaders());
      toast({ title: "Updated", description: "Flag status updated" });
      const res = await axios.get(`${API_URL}/api/admin/safety/red-flags`, getAuthHeaders());
      setFlags(res.data.flags || []);
      setStatusCounts(res.data.status_counts || {});
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-blue-100 text-blue-700 border-blue-300';
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {Object.entries(statusCounts).map(([status, count]) => (
          <Card key={status} className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-sm text-gray-500 capitalize">{status}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-red-500" /> Red Flags</CardTitle></CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {flags.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No red flags reported</p>
            ) : (
              <div className="space-y-3">
                {flags.map(flag => (
                  <div key={flag.id} onClick={() => setSelectedFlag(flag)} className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${getSeverityColor(flag.severity)} ${selectedFlag?.id === flag.id ? 'ring-2 ring-blue-500' : ''}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{flag.child_name || 'Unknown Child'}</p>
                        <p className="text-sm">{flag.category}</p>
                        <p className="text-xs mt-1">Observer: {flag.observer_name || 'Unknown'}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-medium uppercase">{flag.severity}</span>
                        <p className="text-xs mt-1">Level {flag.escalation_level || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle>Flag Details</CardTitle></CardHeader>
          <CardContent>
            {selectedFlag ? (
              <div className="space-y-4">
                <div><p className="text-sm text-gray-500">Child</p><p className="font-medium">{selectedFlag.child_name}</p></div>
                <div><p className="text-sm text-gray-500">Category</p><p className="font-medium">{selectedFlag.category}</p></div>
                <div><p className="text-sm text-gray-500">Description</p><p className="text-sm bg-gray-50 p-3 rounded">{selectedFlag.description}</p></div>
                <div><p className="text-sm text-gray-500">Severity: {selectedFlag.severity} | Escalation Level: {selectedFlag.escalation_level}</p></div>
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateFlag(selectedFlag.id, 'reviewing')}>Start Review</Button>
                    <Button size="sm" variant="outline" onClick={() => updateFlag(selectedFlag.id, 'resolved')} className="text-green-600">Resolve</Button>
                    <Button size="sm" onClick={() => updateFlag(selectedFlag.id, 'escalated', true)} className="bg-red-500 hover:bg-red-600">Escalate</Button>
                    <Button size="sm" variant="outline" onClick={() => updateFlag(selectedFlag.id, 'referred')}>External Referral</Button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Select a flag to view details</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Incident Management Component
function IncidentManagement() {
  const { toast } = useToast();
  const [incidents, setIncidents] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', incident_type: 'safety', severity: 'medium' });

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => { fetchIncidents(); }, []);

  const fetchIncidents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/incidents`, getAuthHeaders());
      setIncidents(res.data.incidents || []);
      setStatusCounts(res.data.status_counts || {});
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createIncident = async () => {
    try {
      const params = new URLSearchParams(formData);
      await axios.post(`${API_URL}/api/admin/incidents?${params}`, null, getAuthHeaders());
      toast({ title: "Created", description: "Incident logged" });
      setShowForm(false);
      setFormData({ title: '', description: '', incident_type: 'safety', severity: 'medium' });
      fetchIncidents();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create", variant: "destructive" });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 sm:mr-4 w-full">
          {Object.entries(statusCounts).map(([status, count]) => (
            <Card key={status} className="border-0 shadow-sm">
              <CardContent className="p-3 text-center"><p className="text-xl font-bold">{count}</p><p className="text-xs text-gray-500 capitalize">{status}</p></CardContent>
            </Card>
          ))}
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-red-500 hover:bg-red-600"><Plus className="w-4 h-4 mr-2" /> Log Incident</Button>
      </div>

      {showForm && (
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader><CardTitle>Log New Incident</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Input placeholder="Incident Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
            <textarea className="w-full border rounded p-2" rows={3} placeholder="Description..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select className="border rounded p-2" value={formData.incident_type} onChange={e => setFormData({...formData, incident_type: e.target.value})}>
                <option value="safety">Safety</option><option value="technical">Technical</option><option value="compliance">Compliance</option><option value="behavioral">Behavioral</option>
              </select>
              <select className="border rounded p-2" value={formData.severity} onChange={e => setFormData({...formData, severity: e.target.value})}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </div>
            <div className="flex gap-3">
              <Button onClick={createIncident} className="bg-red-500 hover:bg-red-600">Log Incident</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>All Incidents</CardTitle></CardHeader>
        <CardContent>
          {incidents.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No incidents logged</p>
          ) : (
            <div className="space-y-3">
              {incidents.map(incident => (
                <div key={incident.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{incident.title}</p>
                      <p className="text-sm text-gray-500">{incident.incident_number}</p>
                      <p className="text-xs text-gray-400">{incident.incident_type} • {incident.severity}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${incident.status === 'open' ? 'bg-red-100 text-red-700' : incident.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {incident.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// AI Guardrails Component
function AIGuardrails() {
  const { toast } = useToast();
  const [guardrails, setGuardrails] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/ai/guardrails`, getAuthHeaders())
      .then(res => setGuardrails(res.data.guardrails))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const updateGuardrail = async (key, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/ai/guardrails?${key}=${value}`, null, getAuthHeaders());
      setGuardrails({...guardrails, [key]: value});
      toast({ title: "Updated", description: "Guardrail setting saved" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-red-500" /> AI Safety Guardrails</CardTitle>
          <CardDescription>These settings ensure AI outputs comply with Sanjaya's ethical guidelines</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { key: 'no_diagnosis', label: 'No Diagnosis', desc: 'AI will never provide medical/psychological diagnoses' },
            { key: 'no_medical_advice', label: 'No Medical Advice', desc: 'AI will never suggest treatments or medications' },
            { key: 'no_therapy_language', label: 'No Therapy Language', desc: 'AI avoids therapeutic terminology' },
            { key: 'no_comparative_assessment', label: 'No Comparisons', desc: 'AI will not compare children against each other' },
            { key: 'no_labeling', label: 'No Labeling', desc: 'AI will not label children (e.g., "problem child")' },
            { key: 'neutral_language_only', label: 'Neutral Language', desc: 'AI uses only observational, neutral language' },
            { key: 'observation_based_only', label: 'Observation-Based', desc: 'AI insights are based only on observations, not interpretations' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
              <button
                onClick={() => updateGuardrail(item.key, !guardrails?.[item.key])}
                className={`w-12 h-6 rounded-full transition-colors ${guardrails?.[item.key] ? 'bg-green-500' : 'bg-red-400'}`}
              >
                <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${guardrails?.[item.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Blocked Terms</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {guardrails?.blocked_terms?.map((term, i) => (
              <span key={i} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">{term}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Required Disclaimers</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {guardrails?.required_disclaimers?.map((d, i) => (
              <p key={i} className="p-3 bg-blue-50 rounded text-sm text-blue-800">"{d}"</p>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Data Privacy Component
function DataPrivacy() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/admin/privacy/settings`, getAuthHeaders()),
      axios.get(`${API_URL}/api/admin/privacy/data-requests`, getAuthHeaders())
    ]).then(([settingsRes, requestsRes]) => {
      setSettings(settingsRes.data.settings);
      setRequests(requestsRes.data.requests || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const updateSetting = async (key, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/privacy/settings?${key}=${value}`, null, getAuthHeaders());
      setSettings({...settings, [key]: value});
      toast({ title: "Updated", description: "Privacy setting saved" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-purple-500" /> Data Retention Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="text-sm font-medium">General Data Retention</label>
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" value={settings?.data_retention_days || 365} onChange={e => updateSetting('data_retention_days', e.target.value)} className="w-24" />
                <span className="text-sm text-gray-500">days</span>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="text-sm font-medium">Session Log Retention</label>
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" value={settings?.session_log_retention_days || 180} onChange={e => updateSetting('session_log_retention_days', e.target.value)} className="w-24" />
                <span className="text-sm text-gray-500">days</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Anonymize on Delete</p>
              <p className="text-sm text-gray-500">Anonymize user data instead of hard deletion</p>
            </div>
            <button onClick={() => updateSetting('anonymize_on_delete', !settings?.anonymize_on_delete)} className={`w-12 h-6 rounded-full ${settings?.anonymize_on_delete ? 'bg-green-500' : 'bg-gray-300'}`}>
              <span className={`block w-5 h-5 bg-white rounded-full shadow transform ${settings?.anonymize_on_delete ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">GDPR Compliant</p>
              <p className="text-sm text-gray-500">Follow GDPR data handling guidelines</p>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">✓ Enabled</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Data Requests</CardTitle></CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No data requests</p>
          ) : (
            <div className="space-y-3">
              {requests.map(req => (
                <div key={req.id} className="p-4 bg-gray-50 rounded-lg flex justify-between">
                  <div><p className="font-medium">{req.type}</p><p className="text-sm text-gray-500">{req.user_email}</p></div>
                  <span className={`px-2 py-1 rounded text-xs ${req.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{req.status}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Audit Logs Component
function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/audit/logs?days=30&limit=100`, getAuthHeaders())
      .then(res => { setLogs(res.data.logs || []); setActionTypes(res.data.action_types || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filteredLogs = filter ? logs.filter(l => l.action_type === filter) : logs;

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <select className="border rounded p-2" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All Actions</option>
          {actionTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-sm text-gray-500">{filteredLogs.length} logs</span>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-indigo-500" /> Audit Trail</CardTitle></CardHeader>
        <CardContent className="max-h-[600px] overflow-y-auto">
          {filteredLogs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No audit logs found</p>
          ) : (
            <div className="space-y-2">
              {filteredLogs.map(log => (
                <div key={log.id} className="p-3 bg-gray-50 rounded flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{log.action}</p>
                    <p className="text-xs text-gray-500">{log.action_type} • {log.user_id}</p>
                    {log.details && <p className="text-xs text-gray-400 mt-1">{log.details}</p>}
                  </div>
                  <span className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// System Health Component
function SystemHealth() {
  const { toast } = useToast();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  const fetchHealth = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/system/health`, getAuthHeaders())
      .then(res => setHealth(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleDataReset = async () => {
    const confirmed = window.confirm(
      'CONFIRM DATA RESET\n\n' +
      'This will permanently delete:\n' +
      '  • All enrolled students/children\n' +
      '  • All sessions, reports, and call logs\n' +
      '  • All subscriptions, insights, and tickets\n\n' +
      'User accounts (observers, principals, parents) will be kept.\n\n' +
      'Type "RESET" in the next prompt to confirm.'
    );
    if (!confirmed) return;
    const typed = window.prompt('Type RESET to confirm:');
    if (typed !== 'RESET') {
      toast({ title: 'Cancelled', description: 'Data reset was not confirmed.', variant: 'destructive' });
      return;
    }
    setResetting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/data/reset`, null, getAuthHeaders());
      toast({ title: 'Data reset complete', description: data.message });
    } catch (err) {
      toast({ title: 'Reset failed', description: err.response?.data?.detail || 'Could not reset data', variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []);

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">System Health</h3>
        <div className="flex gap-2">
          <Button onClick={fetchHealth} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
          <Button
            onClick={handleDataReset}
            variant="outline"
            size="sm"
            disabled={resetting}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {resetting ? 'Resetting...' : 'Reset All Data'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Activity className={`w-8 h-8 mx-auto mb-2 ${(health?.system?.cpu_percent || 0) < 80 ? 'text-green-500' : 'text-red-500'}`} />
            <p className="text-2xl font-bold">{health?.system?.cpu_percent || 0}%</p>
            <p className="text-sm text-gray-500">CPU Usage</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Database className={`w-8 h-8 mx-auto mb-2 ${(health?.system?.memory_percent || 0) < 80 ? 'text-green-500' : 'text-amber-500'}`} />
            <p className="text-2xl font-bold">{health?.system?.memory_percent || 0}%</p>
            <p className="text-sm text-gray-500">Memory</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Database className={`w-8 h-8 mx-auto mb-2 ${(health?.system?.disk_percent || 0) < 80 ? 'text-green-500' : 'text-amber-500'}`} />
            <p className="text-2xl font-bold">{health?.system?.disk_percent || 0}%</p>
            <p className="text-sm text-gray-500">Disk</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <Activity className={`w-8 h-8 mx-auto mb-2 ${health?.database?.healthy ? 'text-green-500' : 'text-red-500'}`} />
            <p className="text-2xl font-bold">{health?.database?.latency_ms || 0}ms</p>
            <p className="text-sm text-gray-500">DB Latency</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle>Service Status</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(health?.services || {}).map(([name, service]) => (
              <div key={name} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium capitalize">{name}</span>
                <span className={`flex items-center gap-1 ${service.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                  {service.status === 'healthy' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {service.status}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Help & FAQs Component
function HelpFAQs() {
  const { toast } = useToast();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '', category: 'general' });

  const getAuthHeaders = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` } });

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/help/faqs`, getAuthHeaders())
      .then(res => setFaqs(res.data.faqs || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const createFAQ = async () => {
    try {
      const params = new URLSearchParams(formData);
      await axios.post(`${API_URL}/api/admin/help/faq?${params}`, null, getAuthHeaders());
      toast({ title: "Created", description: "FAQ added" });
      setShowForm(false);
      setFormData({ question: '', answer: '', category: 'general' });
      const res = await axios.get(`${API_URL}/api/admin/help/faqs`, getAuthHeaders());
      setFaqs(res.data.faqs || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to create FAQ", variant: "destructive" });
    }
  };

  const deleteFAQ = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/help/faq/${id}`, getAuthHeaders());
      setFaqs(faqs.filter(f => f.id !== id));
      toast({ title: "Deleted", description: "FAQ removed" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h3 className="text-lg font-semibold">Help & FAQs</h3><p className="text-sm text-gray-500">Manage help documentation</p></div>
        <Button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white"><Plus className="w-4 h-4 mr-2" /> Add FAQ</Button>
      </div>

      {showForm && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-6 space-y-4">
            <Input placeholder="Question" value={formData.question} onChange={e => setFormData({...formData, question: e.target.value})} />
            <textarea className="w-full border rounded p-2" rows={3} placeholder="Answer" value={formData.answer} onChange={e => setFormData({...formData, answer: e.target.value})} />
            <select className="border rounded p-2" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
              <option value="general">General</option><option value="observer">Observer</option><option value="parent">Parent</option><option value="principal">Principal</option><option value="billing">Billing</option>
            </select>
            <div className="flex gap-3">
              <Button onClick={createFAQ} className="bg-green-500 hover:bg-green-600">Save FAQ</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          {faqs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No FAQs yet</p>
          ) : (
            <div className="space-y-4">
              {faqs.map(faq => (
                <div key={faq.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium">{faq.question}</p>
                      <p className="text-sm text-gray-600 mt-1">{faq.answer}</p>
                      <span className="text-xs text-gray-400 mt-2 inline-block">{faq.category}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteFAQ(faq.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
